<template>
  <v-col class="pa-6">
    <div v-if="!viewingRequest">
      <v-row>
        <v-col cols="12" md="3">
          <v-text-field
            class="input ma-0 pa-0"
            label="Search..."
            prepend-inner-icon="mdi-magnify"
            v-model="search"
            rounded
            :hide-details="true"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="3">
          <v-row class="ma-0" justify="center" justify-md="start">
            <v-select
              style="max-width: 240px"
              class="input ma-0 pa-0"
              label="Status Type"
              v-model="selectedType"
              :items="statusTypes"
              rounded
              :hide-details="true"
              clearable
            ></v-select>
          </v-row>
        </v-col>
      </v-row>
      <v-row justify="center">
        <v-data-table
          style="width: 100%"
          :headers="headers"
          :items="filteredRequests"
          :items-per-page="10"
          class="elevation-1 mt-4 ml-4 mr-8"
        >
          <template v-slot:item.action="{ item }">
            <td>
              <v-icon color="#4390c0" @click="viewRequest(item)"
                >mdi-eye</v-icon
              >
            </td>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip
              class="capatilize"
              :color="statusColor(item.status || 'pending')"
              dark
            >
              {{ item.status || "pending" }}
            </v-chip>
          </template>
        </v-data-table>
      </v-row>
    </div>
    <div v-if="viewingRequest">
      <v-container>
        <!-- Header -->
        <v-row class="align-center mb-4">
          <v-col cols="12" md="6" class="d-flex align-center">
            <v-icon class="black-color mr-2" @click="close()"
              >mdi-arrow-left</v-icon
            >
            <h3 class="grey-color" style="font-weight: 500">Request Info</h3>
          </v-col>
        </v-row>

        <!-- Request Info Card -->
        <v-card class="pa-6 white-card elevation-3 rounded-xl">
          <v-row align="center" class="mb-4">
            <h2 class="fs400 mb-0">Request Info</h2>
            <v-chip
              class="status-chip ml-4"
              :color="statusColor(selectedRequest.status || 'Pending')"
              text-color="white"
              style="text-transform: capitalize"
            >
              {{ selectedRequest.status || "Pending" }}
            </v-chip>
          </v-row>

          <v-divider class="mb-6"></v-divider>

          <v-row>
            <!-- Left Section -->
            <v-col cols="12" md="6">
              <v-row class="mb-3">
                <v-col cols="12" md="6">
                  <p class="font-weight-medium mb-1">Order Number</p>
                  <v-text-field
                    readonly
                    dense
                    outlined
                    rounded
                    v-model="selectedRequest.orderNumber"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row class="mb-3">
                <v-col cols="12" md="6">
                  <p class="font-weight-medium mb-1">Date Created</p>
                  <v-text-field
                    readonly
                    dense
                    outlined
                    rounded
                    v-model="selectedRequest.date"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row class="mb-3">
                <v-col cols="12" md="6">
                  <p class="font-weight-medium mb-1">Customer</p>
                  <v-text-field
                    readonly
                    dense
                    outlined
                    rounded
                    v-model="selectedRequest.customerName"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row class="mb-3">
                <v-col cols="12" md="6">
                  <p class="font-weight-medium mb-1">Delivery Type</p>
                  <v-text-field
                    readonly
                    dense
                    outlined
                    rounded
                    v-model="selectedRequest.deliveryOption"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row class="mb-4">
                <v-col cols="12">
                  <p class="font-weight-medium mb-1">PDF</p>
                  <div>
                    <a
                      v-if="selectedRequest.pdf"
                      :href="selectedRequest.pdf"
                      target="_blank"
                      class="text-primary"
                    >
                      View PDF
                    </a>
                    <p v-else class="grey--text">No PDF uploaded</p>
                  </div>
                </v-col>
              </v-row>
            </v-col>

            <!-- Order Items Section -->
            <v-col>
              <v-row>
                <v-col cols="12">
                  <p class="font-weight-medium mb-2">Order Items</p>
                </v-col>

                <v-col
                  v-for="(item, index) in selectedRequest.items"
                  :key="index"
                  cols="12"
                  sm="6"
                  md="4"
                  lg="3"
                  class="d-flex flex-column align-center mb-4"
                >
                  <v-card
                    outlined
                    class="pa-3 text-center"
                    style="width: 100%; border-radius: 12px"
                  >
                    <v-img
                      v-if="item.image"
                      :src="item.image"
                      max-height="120"
                      max-width="120"
                      contain
                      class="mx-auto mb-3"
                    ></v-img>
                    <div>
                      <p class="text-subtitle-2 font-weight-bold mb-1">
                        {{ item.title || "Unnamed Item" }}
                      </p>
                      <p
                        class="text-body-2 grey--text mb-0"
                        v-if="item.quantity"
                      >
                        Qty: {{ item.quantity }}
                      </p>
                      <p class="text-body-2 grey--text" v-if="item.price">
                        Price: {{ item.price }}
                      </p>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-col>

            <!-- Right Section -->
            <v-col cols="12" md="6">
              <v-row>
                <v-col cols="12" md="8">
                  <p class="font-weight-medium mb-1">Order Status</p>
                  <v-select
                    v-model="selectedRequest.status"
                    :items="statusTypes"
                    dense
                    filled
                    outlined
                    rounded
                    hide-details
                  ></v-select>
                </v-col>
              </v-row>

              <v-row v-if="selectedRequest.status === 'denied'" class="mt-6">
                <v-col cols="12" md="8">
                  <v-textarea
                    v-model="selectedRequest.reason"
                    label="Reason for Denial"
                    auto-grow
                    outlined
                    filled
                    clearable
                    rounded
                    rows="2"
                    :rules="[(v) => !!v || 'Reason is required']"
                  ></v-textarea>
                </v-col>
              </v-row>

              <v-row class="mt-6">
                <v-col cols="12" md="8">
                  <v-btn
                    @click="updateStatus"
                    color="#e022d1"
                    dark
                    rounded
                    block
                    class="pink-btn"
                  >
                    Update Status
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card>
      </v-container>
    </div>
    <!-- Loading Overlay -->
    <v-row justify="center">
      <v-overlay :value="loading">
        <v-progress-circular :size="100" color="#4390c0" indeterminate />
      </v-overlay>
    </v-row>
    <v-dialog v-model="promptDialog" max-width="480">
      <v-card class="pa-8" elevation="4" shaped style="border-radius: 24px">
        <v-card-text class="text-center">
          <v-icon size="96" color="red" v-if="promptType == 'error'">
            mdi-alert-circle
          </v-icon>
          <v-icon size="96" color="green" v-if="promptType == 'success'">
            mdi-check-circle
          </v-icon>
          <div class="text-h5 my-6" v-if="promptType == 'error'">Error</div>
          <div class="text-h5 my-6" v-if="promptType == 'success'">Success</div>
          <div class="body-1 text-h6">{{ promptMessage }}</div>
        </v-card-text>
        <v-card-actions class="justify-center">
          <v-btn
            color="black"
            dark
            depressed
            large
            style="border-radius: 24px"
            @click="promptDialog = false"
          >
            CLOSE
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-col>
</template>

<script>
import {
  collection,
  getDocs,
  getFirestore,
  setDoc,
  query,
  doc,
  addDoc,
  onSnapshot,
} from "firebase/firestore";
import { firebaseApp } from "../../firebaseConfig";
import { getStorage, ref, getDownloadURL } from "firebase/storage";

const db = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);

import { logAction, sendPushNotificationUser } from "@/utils/firestoreUtil";

export default {
  data() {
    return {
      headers: [
        { text: "Order Number", value: "orderNumber" },
        { text: "Customer Name", value: "customerName" },
        { text: "Date Created", value: "date" },
        { text: "Client", value: "clientFullName" },
        { text: "Status", value: "status" },
        { text: "Action", value: "action" },
      ],
      search: "",
      statusTypes: ["all", "pending", "approved", "denied"],
      selectedType: "all",
      requests: [], // now orders array
      selectedRequest: null,
      viewingRequest: false,
      loading: false,
      promptDialog: false,
      promptMessage: "",
      promptType: "",
      clients: [],
      unsubscribeOrders: null,
      unsubscribeClients: null,
    };
  },

  methods: {
    async loadOrderPDF(order) {
      if (order.pdf) {
        try {
          order.pdf = await getDownloadURL(ref(storage, order.pdf));
        } catch (err) {
          console.warn("Failed to load PDF URL:", err);
          order.pdf = null;
        }
      }
    },
    close() {
      this.viewingRequest = false;
      localStorage.removeItem("ViewedRequest");
    },

    viewRequest(item) {
      this.selectedRequest = item;
      this.viewingRequest = true;
      localStorage.setItem(
        "ViewedRequest",
        JSON.stringify(this.selectedRequest)
      );
    },
    getOrders() {
      try {
        this.loading = true;
        const q = query(collection(db, "orders"));

        // Subscribe to real-time updates
        this.unsubscribeOrders = onSnapshot(q, (querySnapshot) => {
          this.requests = [];

          querySnapshot.forEach((docSnap) => {
            let order = docSnap.data();
            order.id = docSnap.id;

            // Convert timestamp
            if (order.createdAt) {
              if (
                typeof order.createdAt === "object" &&
                order.createdAt.seconds
              ) {
                order.date = new Date(
                  order.createdAt.seconds * 1000
                ).toLocaleString();
              } else {
                order.date = order.createdAt;
              }
            } else {
              order.date = "";
            }

            // Ensure proper field mappings
            order.customerName =
              order.customerName || order.clientName || "N/A";
            order.orderNumber = order.orderNumber || order.id;
            if (!order.status) order.status = "pending";

            // Map client info
            order.client = this.clients.find((d) => order.userId === d.id);
            if (order.client) {
              order.clientFullName = `${order.client.firstName} ${order.client.lastName}`;
            } else {
              order.clientFullName = "N/A";
            }

            this.requests.push(order);
          });

          console.log("Orders mapped:", this.requests);
          this.loading = false;
        });
      } catch (error) {
        this.loading = false;
        console.error("Error fetching orders:", error);
      }
    },
    statusColor(status) {
      switch (status) {
        case "denied":
          return "#e022d1";
        case "pending":
          return "#4390c0";
        case "approved":
          return "green";
        default:
          return "grey";
      }
    },
    async updateStatus() {
      try {
        if (
          this.selectedRequest.status === "denied" &&
          !this.selectedRequest.reason
        ) {
          this.promptMessage = "Please provide a reason for denial.";
          this.promptType = "error";
          this.promptDialog = true;
          return;
        }

        this.loading = true;

        await setDoc(doc(db, "orders", this.selectedRequest.id), {
          ...this.selectedRequest,
          reason:
            this.selectedRequest.status === "denied"
              ? this.selectedRequest.reason
              : "",
        });

        this.loading = false;

        if (this.selectedRequest.status === "denied") {
          if (this.selectedRequest.client.fcmToken) {
            let res = await sendPushNotificationUser(
              "Order Declined",
              `Your order ${
                this.selectedRequest.orderNumber || ""
              } has been declined for the following reason: ${
                this.selectedRequest.reason
              }`,
              this.selectedRequest.client.fcmToken
            );
          }
          this.addNotification(
            this.selectedRequest.userId,
            "Order Declined",
            `Your order ${
              this.selectedRequest.orderNumber || ""
            } has been declined for the following reason: ${
              this.selectedRequest.reason
            }`
          );
        } else if (this.selectedRequest.status === "approved") {
          if (this.selectedRequest.client.fcmToken) {
            let res = await sendPushNotificationUser(
              "Order Approved",
              `Your order ${
                this.selectedRequest.orderNumber || ""
              } has been approved.`,
              this.selectedRequest.client.fcmToken
            );
          }
          this.addNotification(
            this.selectedRequest.userId,
            "Order Approved",
            `Your order ${
              this.selectedRequest.orderNumber || ""
            } has been approved.`
          );
        }

        await this.logStatusUpdate();

        this.promptMessage = "Successfully updated order status!";
        this.promptType = "success";
        this.promptDialog = true;

        setTimeout(() => {
          this.promptDialog = false;
        }, 3000);
      } catch (error) {
        this.loading = false;
        console.error("Error updating order status:", error);
        this.promptMessage =
          "An error occurred while updating the order status.";
        this.promptType = "error";
        this.promptDialog = true;
      }
    },
    async logStatusUpdate() {
      await logAction(
        "Order Management",
        "Status Update",
        this.loggedInUser.email,
        `${this.loggedInUser.email} updated order ${
          this.selectedRequest.id
        } status to ${this.selectedRequest.status}. Reason: ${
          this.selectedRequest.reason || "N/A"
        }`
      );
    },
    getClients() {
      try {
        const q = query(collection(db, "clients"));
        this.loading = true;

        // Subscribe to real-time updates
        this.unsubscribeClients = onSnapshot(q, (querySnapshot) => {
          this.clients = []; // Clear previous list

          querySnapshot.forEach((docSnap) => {
            const tempClient = docSnap.data();
            tempClient.id = docSnap.id;
            this.clients.push(tempClient);
          });

          this.loading = false;
        });
      } catch (error) {
        this.loading = false;
        console.error("Error fetching clients:", error);
      }
    },
    async addNotification(userID, type, message) {
      const notification = {
        createdAt: new Date(),
        userType: "Doctor",
        userID,
        type,
        message,
      };

      try {
        this.loading = true;
        await addDoc(collection(db, "alerts"), notification);
        this.loading = false;
      } catch (error) {
        console.error("Error adding notification:", error);
        this.loading = false;
      }
    },
  },
  async created() {
    await this.getClients();
    await this.getOrders();

    if (localStorage.getItem("ViewedRequest")) {
      const selectedRequest = JSON.parse(localStorage.getItem("ViewedRequest"));
      const newRequest = this.requests.find(
        (req) => req.id === selectedRequest.id
      );
      if (newRequest) {
        this.selectedRequest = newRequest;
        this.viewingRequest = true;
      }
    }
  },

  computed: {
    filteredRequests() {
      return this.requests.filter((item) => {
        const matchesStatus =
          this.selectedType === "all" ||
          (item.status &&
            item.status.toLowerCase() === this.selectedType.toLowerCase());

        const matchesSearch = Object.values(item).some((value) => {
          if (value != null) {
            return value
              .toString()
              .toLowerCase()
              .includes(this.search.toLowerCase());
          }
          return false;
        });

        return matchesStatus && matchesSearch;
      });
    },

    loggedInUser() {
      return this.$store.state.user || {};
    },
  },
  beforeUnmount() {
    if (this.unsubscribeOrders) {
      this.unsubscribeOrders();
    }

    if (this.unsubscribeClients) {
    this.unsubscribeClients();
  }
  },
};
</script>

<style scoped>
.reason-field {
  background-color: #fafafa;
  border-radius: 12px;
}

.image {
  border-radius: 12px;
  border: 2px solid #ccc;
  transition: 0.3s;
}

.image:hover {
  transform: scale(1.05);
}
</style>
